// public/js/features/devices/branded-models.js
// Superb-branded product names shown to customers, mapped from the underlying
// hardware. Customers must NEVER see the vendor model name (Oyster3, ELA, ...),
// so every customer-facing label and the device manual resolve through here.
// The real model stays internal only (battery coefficients, support). The single
// source of truth for the device manual (docs/device-manual.md).

// Battery family keys refer to public/js/features/tracker-config/battery.js.
// `image` is the customer-safe product photo (brand-free artwork on the shared
// static origin, versioned via deviceAssetUrl) - the single source shared by the
// device manual and the device thumbnails. Vendor-branded raw photos are never used.
export const BRANDED_PRODUCTS = {
  tracepro:  { key: 'tracepro',  name: 'Superb TracePro',  slug: 'tracepro',  category: 'tracker', icon: 'fa-plane-up',        tracker: true, batteryFamily: 'griffinAir', accent: '#0d6efd', image: 'tracepro-normal.png' },
  locatepro: { key: 'locatepro', name: 'Superb LocatePro', slug: 'locatepro', category: 'tracker', icon: 'fa-location-crosshairs', tracker: true, batteryFamily: 'oyster3', accent: '#0dcaf0', image: 'locatepro-normal.png' },
  smart:     { key: 'smart',     name: 'Superb Smart+',    slug: 'smart',     category: 'tag',     icon: 'fa-tag',             tracker: false, accent: '#6f42c1', image: 'smart-normal.png' },
  temp:      { key: 'temp',      name: 'Superb Temp+',     slug: 'temp',      category: 'sensor',  icon: 'fa-temperature-half', tracker: false, accent: '#fd7e14', image: 'temp-normal.png' },
  humidity:  { key: 'humidity',  name: 'Superb Humidity+', slug: 'humidity',  category: 'sensor',  icon: 'fa-droplet',         tracker: false, accent: '#20c997', image: 'humidity-normal.png' },
  engine:    { key: 'engine',    name: 'Superb Engine+',   slug: 'engine',    category: 'beacon',  icon: 'fa-gears',           tracker: false, accent: '#6c757d', image: 'ibs09r-normal.png' },
  shock:     { key: 'shock',     name: 'Superb Shock+',    slug: 'shock',     category: 'feature', icon: 'fa-burst',           tracker: false, accent: '#dc3545', image: null },
};

export const BRANDED_ORDER = ['tracepro', 'locatepro', 'smart', 'temp', 'humidity', 'engine', 'shock'];

// Real hardware -> branded key. ONLY the models actually sold under a Superb
// brand. Everything else (Oyster Edge 92, Barra GPS 110, ...) is an internal
// test device with no customer brand, and stays anonymous in the UI.
const PRODID_TO_BRAND = { 118: 'tracepro', 87: 'locatepro', 102: 'locatepro' };
// Both Ingics distance rangers (iBS03R and the newer iBS09R) are the same product
// to the customer - Superb Engine+ - even though their raw payloads differ.
const BLE_SUBTYPE_TO_BRAND = { 'ELA-RHT': 'temp', 'Ingics-iBS03R': 'engine', 'Ingics-iBS09R': 'engine' };

export function brandKeyForDevice(d) {
  if (!d) return null;
  if (d.type === 'digitalmatter') { const p = d.metadata?.prodId; return (p != null && PRODID_TO_BRAND[p]) || null; }
  if (d.type === 'bluetooth') return BLE_SUBTYPE_TO_BRAND[d.subType] || null;
  return null;
}

export function brandedProductForDevice(d) {
  const k = brandKeyForDevice(d);
  return k ? BRANDED_PRODUCTS[k] : null;
}

// Manual anchor for a device, or null if the device has no branded product page
// (e.g. an internal test device). Route form: #manual/devices/<slug>.
export function manualHrefForDevice(d) {
  const b = brandedProductForDevice(d);
  return b ? `#manual/devices/${b.slug}` : null;
}
export const manualHrefForSlug = (slug) => `#manual/devices/${slug}`;

// Battery-family key (battery.js) -> branded product. Only the two families sold
// under a Superb tracker brand; internal test families (oysterEdge, barraGps, ...)
// map to nothing, so they never get a customer-facing battery panel.
export const BATTERY_FAMILY_TO_BRAND = { griffinAir: 'tracepro', oyster3: 'locatepro' };
export function brandLabelForFamily(key) {
  const b = BATTERY_FAMILY_TO_BRAND[key];
  return b ? BRANDED_PRODUCTS[b].name : null;
}
// The families a customer may pick in a battery estimate, labelled by brand.
export const BRANDED_TRACKER_FAMILIES = [
  { key: 'griffinAir', label: BRANDED_PRODUCTS.tracepro.name },
  { key: 'oyster3',    label: BRANDED_PRODUCTS.locatepro.name },
];
